import { useLiveQuery } from "dexie-react-hooks";
import { db } from "./db";

/**
 * Check if a product is in today's meals
 * @param productId The product ID to check
 * @returns Promise<boolean> True if the product is in today's meals, false otherwise
 */
export async function isProductInTodaysMeals(productId: string): Promise<boolean> {
  if (!productId) return false;

  const today = new Date().toISOString().split("T")[0]; // Get YYYY-MM-DD format
  const meals = await db.meals.filter((meal) => meal.eatenAt.startsWith(today)).toArray();

  return meals.some((meal) => meal.products.some((product) => product.productId === productId));
}

/**
 * React hook to get the count of times a product appears in today's meals
 * @param productId The product ID to check
 * @returns number | null - Count of times the product appears in today's meals, or null while loading
 */
export function useProductMealCountToday(productId?: string): number | null {
  const today = new Date().toISOString().split("T")[0];

  // Use Dexie's useLiveQuery to automatically update when meals change
  const mealCount = useLiveQuery(async () => {
    try {
      const meals = await db.meals.where("eatenAt").startsWith(today).toArray();

      if (productId) {
        // Count all occurrences of the product in today's meals
        return meals.reduce((count, meal) => {
          return count + meal.products.filter((p) => p.productId === productId).length;
        }, 0);
      } else {
        return meals.flatMap((meal) => meal.products).length;
      }
    } catch (error) {
      console.error("Error counting product in today's meals:", error);
      return 0;
    }
  }, [productId, today]);

  // Return null while loading for the first time
  return mealCount === undefined ? null : mealCount;
}

// For backward compatibility
export function useIsInTodaysMeals(productId: string): boolean | null {
  const count = useProductMealCountToday(productId);
  return count === null ? null : count > 0;
}
